// product_type helpers — the sibling of proof.ts, for the OTHER config key that
// both feeds read off a line: the one Nexus routes by.
//
// `config.product_type` is the canonical Service.family_key. Nexus resolves
// every imported line by it, so a line without one is unroutable there: it
// matches no Service and lands as a rep-visible "catalog could not match"
// cleanup Note rather than being silently dropped.
//
// A line without one is also not a customer selection. The Print door seeds a
// new quote with a single `config: {}` line so STEP 1's product-type picker has
// a row to render, and "add another project" persists that same empty shape so
// artwork can attach to a QuoteLine by id before anything is chosen. Both are
// deliberate; both stay. The portal keeps the row and transmits nothing for it.
// A line that DOES carry a product_type transmits in full, unchanged, including
// every key Nexus has not mirrored as a ServiceAttribute yet.
//
// The test here is schema-free by design — the presence of the routing key, not
// the completeness of the config. See .claude/rules/nexus-contract.md.
import { PROOFED_PRODUCT_TYPES, customPackagingCarriesArt, mediaLineCarriesArt } from '../vocabulary.js';
// Read a line's product_type from its `config` blob, narrowed to a string.
// Returns '' when the line carries no (string) product_type — the same
// "no family" value the price-side callers already hand to
// lineNeedsHandsOnPrep / lineMissingUploadFieldKeys.
export function readLineProductType(config) {
    if (typeof config !== 'object' || config === null)
        return '';
    const raw = config['product_type'];
    return typeof raw === 'string' ? raw : '';
}
// True when a line carries a usable routing key — i.e. the customer picked a
// product type on it. Whitespace does not count: Nexus matches family_key
// exactly, so a blank-ish value resolves to no Service just as an absent one
// does, and must not cross either.
export function isConfiguredLine(config) {
    return readLineProductType(config).trim() !== '';
}
// Two quote-level questions about a line SET. They are separate predicates
// because two of the product tiles — `assembly` and `something_else` —
// are not printed or copied-media things (ADR-019), and each question answers
// differently for them. A single "does this quote have any line?" proxy answers
// both wrongly, in opposite directions.
// Is there anything on this order to PROOF? Read by the submit gate and the SPA
// pre-flight to tell a cleared proof selection — which must block — apart from an
// order with nothing proofable on it, which must not. An `assembly` item and a
// `something_else` item are both invisible to `proof_type` (it is not offered to
// either family), so a quote holding only those resolves to `proof_type: 'none'`
// with no field anywhere the customer could answer: blocking there strands them
// on an error they cannot clear.
//
// A media line with no printed or etched art is the same case at LINE grain:
// the seed hides `proof_type` on it, so it answers nothing (FB-0094). So is a
// custom packaging line that does not print: a stock carton or a plain custom box.
export function quoteHasProofableLine(lineConfigs) {
    return lineConfigs.some(lineIsProofed);
}
// Is this line asked how it is proofed? The per-line half of the seed's
// `proof_type` gate — a proofed family, and on a media or custom packaging line, art to
// proof — so the gates and the quote-level proof intent agree with what the form
// shows.
export function lineIsProofed(config) {
    const productType = readLineProductType(config);
    if (!PROOFED_PRODUCT_TYPES.includes(productType))
        return false;
    if (productType === 'media_replication')
        return mediaLineCarriesArt(config);
    if (productType === 'custom_packaging')
        return customPackagingCarriesArt(config);
    return true;
}
//# sourceMappingURL=product-type.js.map